import { ArrowRight, ChevronDown } from "lucide-react";
import { useState } from "react";
import { DEMO_USERS } from "@/db/demo-users";
import { isDeployedHost } from "@/lib/environment";

type Props = {
  onSelect: (email: string) => void | Promise<void>;
  disabled?: boolean;
};

// Show the sandbox-account panel everywhere except the deployed staging and
// production hosts.
function shouldShowDemoAccounts(): boolean {
  if (typeof window === "undefined") return false;
  return !isDeployedHost(window.location.hostname);
}

export function DemoAccountsPanel({ onSelect, disabled }: Props) {
  const [open, setOpen] = useState(false);

  if (!shouldShowDemoAccounts()) return null;

  return (
    <div className="mt-5 rounded-md border border-dashed border-border bg-muted px-3.5 py-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 py-1.5 text-left"
      >
        <span className="font-display flex items-center gap-2 text-xs font-bold tracking-[0.22em] text-foreground uppercase">
          <span className="inline-flex items-center rounded-[4px] bg-[var(--intensity-8)] px-[7px] py-[2px] text-xs tracking-[0.14em] text-white">
            Demo
          </span>
          <span>Try as — sandbox accounts</span>
        </span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <>
          <div className="mt-1 mb-2 text-sm font-medium text-muted-foreground">
            read-only data · reset hourly
          </div>
          <ul className="m-0 list-none p-0">
            {DEMO_USERS.map((u, idx) => (
              <li key={u.email} className={idx === 0 ? "" : "border-t border-border"}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(u.email)}
                  className="group grid w-full grid-cols-[32px_1fr_auto_14px] items-center gap-2.5 rounded-md px-1.5 py-2 text-left transition-[background,box-shadow,border-radius] duration-100 hover:rounded-lg hover:bg-white hover:shadow-[0_2px_8px_rgba(0,24,71,0.06)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span
                    className="font-display flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold tracking-[0.04em] text-white"
                    style={{ background: u.avatarGradient }}
                  >
                    {u.initials}
                  </span>
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="truncate text-sm leading-tight font-semibold text-foreground">
                      {u.name}
                    </span>
                    <span className="truncate text-sm text-muted-foreground">{u.meta}</span>
                  </span>
                  <span
                    className={
                      u.roleLabel === "Admin"
                        ? "font-display rounded-[4px] bg-brand-900 px-[7px] py-[3px] text-xs font-bold tracking-[0.14em] text-white uppercase"
                        : "font-display rounded-[4px] bg-secondary px-[7px] py-[3px] text-xs font-bold tracking-[0.14em] text-foreground uppercase"
                    }
                  >
                    {u.roleLabel}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-[transform,color] duration-100 group-hover:translate-x-0.5 group-hover:text-[var(--ring)]" />
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
